import React, { useEffect, useState } from "react";
import { checkAuthAndRedirect } from "../../api/auth";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  CircularProgress,
  Container,
  Paper,
  ListItemIcon,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  Fade
} from "@mui/material";

// アイコン
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import LogoutIcon from '@mui/icons-material/Logout';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";

const SettingsHome = () => {
  const navigate = useNavigate();
  const [sharedView, setSharedView] = useState(null); // 初期は読み込み中
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);

      // 認証チェックとユーザー情報取得（ログインしてなければリダイレクト）
      const userData = await checkAuthAndRedirect(navigate);
      if (!userData) return;

      setUserInfo(userData);

      // 共有状態の取得
      const sharedRes = await fetch("/api/shared/view", {
        headers: {
          Authorization: "Bearer " + sessionStorage.getItem("token"),
        },
      });
      const sharedData = await sharedRes.json();
      setSharedView(sharedData.sharedView);

    } catch (err) {

      setSharedView(false);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem("token");
    navigate("/login");
  };

  const handleAccountSwitch = () => {
    window.location.href =
      `https://accounts.google.com/o/oauth2/v2/auth?client_id=683384738133-ct55hc4h317b1h9to83kthp71g6qqeaf.apps.googleusercontent.com&redirect_uri=https://supply-assist.com/login/oauth2/code/google&response_type=code&scope=openid%20email%20profile&prompt=select_account`;
  };

  // 読み込み中
  if (loading) {
    return (
      <>
        <Header />
        <Container 
          maxWidth="md" 
          sx={{ 
            mt: 4, 
            display: 'flex', 
            justifyContent: 'center',
            // PWAボトムナビゲーション対応
            pb: { xs: 12, sm: 6 },
            minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
          }}
        >
          <CircularProgress color="primary" />
        </Container>
      </>
    );
  }

  const settingsItems = [
    { 
      label: "アカウント情報", 
      path: "/setting-Account", 
      icon: <AccountCircleIcon color="primary" />,
      description: "ユーザー名、メールアドレスなどの基本情報を確認できます"
    },
    ...(!sharedView ? [{ 
      label: "プラン管理", 
      path: "/settings/plan", 
      icon: <WorkIcon color="primary" />,
      description: "ご利用中のプランの確認や変更ができます"
    }] : []),
    { 
      label: "共有設定", 
      path: "/settings/shared", 
      icon: <PeopleIcon color="primary" />,
      description: "顧客データや補充履歴の共有設定ができます"
    },
    { 
      label: "アカウントを切り替える", 
      action: handleAccountSwitch, 
      icon: <SwitchAccountIcon color="primary" />,
      description: "別のGoogleアカウントでログインします"
    },
    { 
      label: "ログアウト", 
      action: handleLogout, 
      icon: <LogoutIcon color="error" />,
      description: "安全にログアウトします"
    }
  ];

  return (
    <>
      <Header />
      <Container 
        maxWidth="md" 
        sx={{ 
          mt: 4, 
          // PWAボトムナビゲーション対応：安全マージンを確保
          mb: { xs: 2, sm: 6 }, 
          pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
          px: { xs: 2, sm: 3 },
          // 最小高さを確保してスクロール可能にする
          minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
        }}
      >
        {/* パンくずリスト */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={() => navigate('/dashboard')}
            style={{ cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            ホーム
          </Link>
          <Typography
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}
            color="text.primary"
          >
            <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            設定
          </Typography>
        </Breadcrumbs>

        <Fade in={true}>
          <Box>
            {/* ヘッダーセクション */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  設定
                </Typography>
                <Typography color="text.secondary">
                  アプリケーションの設定やアカウント情報の管理
                </Typography>
              </Box>
              
              {/* ログイン中のユーザー情報カード */}
              {userInfo && (
                <Card sx={{ 
                  maxWidth: { xs: '100%', sm: 300 },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <CardContent sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    '&:last-child': { pb: 1.5 }
                  }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                      {userInfo.username ? userInfo.username.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {userInfo.username || 'ユーザー名'}
                        {sharedView && (
                          <Tooltip title="共有アカウントとして参加中">
                            <Box 
                              component="span" 
                              sx={{ 
                                display: 'inline-block',
                                ml: 1,
                                px: 1, 
                                py: 0.2, 
                                bgcolor: 'primary.light', 
                                color: 'primary.main',
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                fontWeight: 'bold'
                              }}
                            >
                              共有
                            </Box>
                          </Tooltip>
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {userInfo.email || 'メールアドレス'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>

            <Divider sx={{ mb: 4 }} />
            
            {/* 設定メニューリスト */}
            <Paper 
              elevation={2} 
              sx={{ 
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <List sx={{ p: 0 }}>
                {settingsItems.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItemButton
                      onClick={() => item.action ? item.action() : navigate(item.path)}
                      sx={{ 
                        py: 2,
                        px: 3,
                        transition: 'all 0.2s',
                        '&:hover': { 
                          bgcolor: item.label === 'ログアウト' ? 
                            'rgba(244, 67, 54, 0.04)' : 'rgba(33, 150, 243, 0.04)' 
                        }
                      }}
                    >
                      <ListItemIcon>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography fontWeight="medium">
                            {item.label}
                          </Typography>
                        } 
                        secondary={item.description}
                        primaryTypographyProps={{ 
                          color: item.label === 'ログアウト' ? 'error' : 'text.primary' 
                        }}
                      />
                      <NavigateNextIcon color="action" />
                    </ListItemButton>
                    {index < settingsItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
            
            {/* ヘルプセクション */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                mt: 3, 
                bgcolor: 'info.light', 
                color: 'info.dark',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2
              }}
            >
              <HelpOutlineIcon fontSize="large" />
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  お問い合わせ
                </Typography>
                <Typography variant="body2">
                  設定や操作方法についてご不明な点がございましたら、
                  <Link href="mailto:support@supply-assist.com" fontWeight="medium">
                    support@supply-assist.com
                  </Link> 
                  までお問い合わせください。
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </>
  );
};

export default SettingsHome;